import React, { useEffect, useState, useRef } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { StatusBadge } from "@/components/StatusBadge";
import {
  STATUS_LABELS,
  URGENCY_LABELS,
  URGENCY_STYLES,
  fmtDateShort,
  fmtRupiah,
} from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Plus, FileWarning, Download, FileText, Trash2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function Reports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [divisionId, setDivisionId] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCost, setTotalCost] = useState(0);
  const PAGE_SIZE = 20;

  const load = async (targetPage = page) => {
    setLoading(true);
    const params = { page: targetPage, page_size: PAGE_SIZE };
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    if (divisionId !== "all") params.division_id = divisionId;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    try {
      const { data } = await api.get("/reports", { params });
      setReports(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setTotalCost(data.total_cost || 0);
    } catch {
      toast.error("Gagal memuat laporan. Coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/divisions").then(({ data }) => setDivisions(data)).catch(() => {});
  }, []);

  const firstRender = useRef(true);
  useEffect(() => {
    setSelected([]);
    const delay = firstRender.current ? 0 : 300;
    firstRender.current = false;
    const t = setTimeout(() => {
      setPage(1);
      load(1);
    }, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, status, divisionId, dateFrom, dateTo]);

  const goToPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
    setSelected([]);
    load(p);
  };

  const buildParams = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status !== "all") params.set("status", status);
    if (divisionId !== "all") params.set("division_id", divisionId);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    return params.toString();
  };

  const exportExcel = () => window.open(`${API}/reports/export/excel?${buildParams()}`, "_blank");
  const exportPdf = () => window.open(`${API}/reports/export/pdf?${buildParams()}`, "_blank");

  const iso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const setPreset = (type) => {
    const now = new Date();
    let from, to;
    if (type === "this") {
      from = new Date(now.getFullYear(), now.getMonth(), 1);
      to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else {
      from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      to = new Date(now.getFullYear(), now.getMonth(), 0);
    }
    setDateFrom(iso(from));
    setDateTo(iso(to));
  };

  const isAdmin = user?.role === "admin";

  const toggleSelect = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleSelectAll = () =>
    setSelected((prev) => (prev.length > 0 ? [] : reports.map((r) => r.id)));

  const confirmDelete = async () => {
    const target = pendingDelete;
    setPendingDelete(null);
    try {
      if (target === "bulk") {
        const { data } = await api.post("/reports/bulk-delete", { ids: selected });
        toast.success(`${data.deleted} laporan dihapus`);
        setSelected([]);
      } else {
        await api.delete(`/reports/${target.id}`);
        toast.success(`Laporan ${target.report_number} dihapus`);
        setSelected((prev) => prev.filter((x) => x !== target.id));
      }
      // if we just deleted the last item(s) on a page beyond the first, step back
      const nextPage = reports.length <= (target === "bulk" ? selected.length : 1) && page > 1 ? page - 1 : page;
      if (nextPage !== page) setPage(nextPage);
      load(nextPage);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus laporan");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900 md:text-5xl">
            Laporan Kerusakan
          </h1>
          <p className="mt-2 text-base text-slate-500">
            Daftar seluruh laporan yang dapat Anda akses.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            data-testid="export-excel-btn"
            variant="outline"
            className="rounded-full"
            onClick={exportExcel}
          >
            <Download className="mr-1 h-4 w-4" /> Ekspor Excel
          </Button>
          <Button
            data-testid="export-pdf-btn"
            variant="outline"
            className="rounded-full"
            onClick={exportPdf}
          >
            <FileText className="mr-1 h-4 w-4" /> Ekspor PDF
          </Button>
          {(user?.role === "pelapor" || user?.role === "admin") && (
            <Button
              data-testid="new-report-btn"
              className="rounded-full bg-[var(--sipkd-primary)] px-5 text-white transition-colors duration-300 hover:bg-[var(--sipkd-primary-hover)]"
              onClick={() => navigate("/reports/new")}
            >
              <Plus className="mr-1 h-4 w-4" /> Lapor Kerusakan
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 bg-white p-3 shadow-sm">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="search-input"
            placeholder="Cari no. laporan / plat / deskripsi…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[190px]" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(user?.role === "admin") && (
          <Select value={divisionId} onValueChange={setDivisionId}>
            <SelectTrigger className="w-[170px]" data-testid="filter-division">
              <SelectValue placeholder="Divisi" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Divisi</SelectItem>
              {divisions.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-slate-200">
            <button
              data-testid="preset-this-month"
              onClick={() => setPreset("this")}
              className="border-r border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              Bulan Ini
            </button>
            <button
              data-testid="preset-last-month"
              onClick={() => setPreset("last")}
              className="bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              Bulan Lalu
            </button>
          </div>
          <div className="w-[150px]">
            <DatePicker testId="filter-date-from" value={dateFrom} onChange={setDateFrom} placeholder="Dari tanggal" />
          </div>
          <span className="text-slate-400">–</span>
          <div className="w-[150px]">
            <DatePicker testId="filter-date-to" value={dateTo} onChange={setDateTo} placeholder="Sampai tanggal" />
          </div>
          {(dateFrom || dateTo) && (
            <Button
              data-testid="clear-date-btn"
              variant="ghost"
              size="sm"
              className="text-slate-500"
              onClick={() => { setDateFrom(""); setDateTo(""); }}
            >
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* Total biaya summary */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">
            Total {total} laporan{(dateFrom || dateTo) ? " (sesuai filter)" : ""}
          </span>
          {isAdmin && selected.length > 0 && (
            <Button
              data-testid="bulk-delete-btn"
              variant="destructive"
              size="sm"
              onClick={() => setPendingDelete("bulk")}
            >
              <Trash2 className="mr-1 h-4 w-4" /> Hapus {selected.length} laporan
            </Button>
          )}
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wider text-slate-500">Total Biaya Perbaikan</p>
          <p className="font-num text-xl font-bold text-[var(--sipkd-primary)]" data-testid="total-cost">
            {fmtRupiah(totalCost)}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="sipkd-panel overflow-hidden rounded-3xl p-3 sm:p-5">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="thin-scroll overflow-x-auto">
          <table className="w-full min-w-[1020px] table-fixed text-left text-sm">
            <colgroup>
              {isAdmin && <col className="w-12" />}
              <col className="w-[132px]" />
              <col className="w-[120px]" />
              <col />
              <col className="w-[150px]" />
              <col className="w-[110px]" />
              <col className="w-[190px]" />
              <col className="w-[110px]" />
              {isAdmin && <col className="w-16" />}
            </colgroup>
            <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.12em] text-slate-500">
              <tr>
                {isAdmin && (
                  <th className="px-4 py-3.5">
                    <Checkbox
                      data-testid="select-all-reports"
                      checked={selected.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                  </th>
                )}
                <th className="px-4 py-3.5 font-semibold">No. Laporan</th>
                <th className="px-4 py-3.5 font-semibold">Plat</th>
                <th className="px-4 py-3.5 font-semibold">Deskripsi</th>
                <th className="px-4 py-3.5 font-semibold">Divisi</th>
                <th className="px-4 py-3.5 text-center font-semibold">Urgensi</th>
                <th className="px-4 py-3.5 text-center font-semibold">Status</th>
                <th className="px-4 py-3.5 text-right font-semibold">Tanggal</th>
                {isAdmin && <th className="px-4 py-3.5 text-center font-semibold">Aksi</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={isAdmin ? 9 : 7} className="px-4 py-10 text-center text-slate-400">
                    Memuat…
                  </td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 9 : 7} className="px-4 py-14 text-center">
                    <FileWarning className="mx-auto h-8 w-8 text-slate-300" />
                    <p className="mt-2 text-sm text-slate-400">Belum ada laporan.</p>
                  </td>
                </tr>
              ) : (
                reports.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={`report-row-${r.id}`}
                    onClick={() => navigate(`/reports/${r.id}`)}
                    className="cursor-pointer transition-colors duration-200 hover:bg-slate-50"
                  >
                    {isAdmin && (
                      <td className="px-4 py-4 align-middle" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          data-testid={`select-report-${r.report_number}`}
                          checked={selected.includes(r.id)}
                          onCheckedChange={() => toggleSelect(r.id)}
                        />
                      </td>
                    )}
                    <td className="whitespace-nowrap px-4 py-4 align-middle font-num text-sm font-semibold text-[var(--sipkd-primary)]">
                      {r.report_number}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 align-middle">
                      <span className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 font-num text-xs font-bold tracking-wide text-slate-700">
                        {r.plate_number}
                      </span>
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <p className="truncate text-slate-700">{r.damage_description}</p>
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <p className="truncate text-slate-600">{r.division_name || "-"}</p>
                    </td>
                    <td className="px-4 py-4 text-center align-middle">
                      <span
                        className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${URGENCY_STYLES[r.urgency]}`}
                      >
                        {URGENCY_LABELS[r.urgency]}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center align-middle">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-right align-middle font-num text-xs text-slate-500">
                      {fmtDateShort(r.created_at)}
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-4 text-center align-middle" onClick={(e) => e.stopPropagation()}>
                        <button
                          data-testid={`delete-report-${r.report_number}`}
                          onClick={() => setPendingDelete(r)}
                          title="Hapus laporan"
                          className="rounded-full p-2 text-slate-400 transition-colors duration-200 hover:bg-rose-50 hover:text-rose-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-3" data-testid="pagination-controls">
          <p className="text-sm text-slate-500">
            Halaman <span className="font-num font-semibold text-slate-700">{page}</span> dari{" "}
            <span className="font-num font-semibold text-slate-700">{totalPages}</span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              data-testid="prev-page-btn"
              variant="outline"
              size="sm"
              className="rounded-full"
              disabled={page <= 1 || loading}
              onClick={() => goToPage(page - 1)}
            >
              Sebelumnya
            </Button>
            <Button
              data-testid="next-page-btn"
              variant="outline"
              size="sm"
              className="rounded-full"
              disabled={page >= totalPages || loading}
              onClick={() => goToPage(page + 1)}
            >
              Berikutnya
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus laporan?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete === "bulk"
                ? `${selected.length} laporan akan dihapus permanen beserta notifikasinya.`
                : `Laporan ${pendingDelete?.report_number} akan dihapus permanen. Tindakan ini tidak dapat dibatalkan.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Batal</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-btn"
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDelete}
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
